import React, { useState, useEffect, useCallback } from "react";

import {
  listOAuthApps,
  upsertOAuthApp,
  deleteOAuthApp,
  listOAuthProviders,
} from "../../../../api";
import { useNotify } from "../../../../ToastContext";
import { usePermission } from "../../../../UserContext";
import PasswordInput from "../../../../components/PasswordInput";
import xmarkIcon from "../../../../assets/xmark.svg";
import { ProviderBadge } from "./providerIcons";

// ── Per-deployment OAuth App configuration ───────────────────────────
// IT admin pastes the client_id / client_secret from each provider's
// developer console (api.slack.com/apps, console.cloud.google.com,
// notion.so/my-integrations, ...) here. The credentials service reads
// from this table first, falls back to env vars when no row exists, so
// config-as-code deployments still work.

// Per-provider setup guidance shown in the OAuth App config modal. Each
// entry has a deep link to the right page in the provider's developer
// console plus a one-liner pointing the operator at where the redirect URI
// goes — saves them having to read the full provider docs to find it.
const PROVIDER_DOC_LINKS = {
  slack: {
    consoleURL: "https://api.slack.com/apps",
    label: "Slack API Apps",
    redirectHint:
      "Paste under \u201cOAuth & Permissions\u201d \u2192 Redirect URLs. Add the bot scopes chat:write, channels:read, groups:read.",
  },
  google: {
    consoleURL: "https://console.cloud.google.com/apis/credentials",
    label: "Google Cloud Credentials",
    redirectHint:
      "Pick \u201cOAuth client ID\u201d \u2192 Web application. Paste under \u201cAuthorized redirect URIs\u201d.",
  },
  notion: {
    consoleURL: "https://www.notion.so/my-integrations",
    label: "Notion Integrations",
    redirectHint:
      "Make a Public integration, then paste under \u201cOAuth Domain & URIs\u201d \u2192 Redirect URIs.",
  },
  github: {
    consoleURL: "https://github.com/settings/developers",
    label: "GitHub Developer Settings",
    redirectHint:
      "New OAuth App. Paste as the \u201cAuthorization callback URL\u201d.",
  },
  microsoft: {
    consoleURL: "https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade",
    label: "Azure App Registrations",
    redirectHint:
      "New registration \u2192 \u201cWeb\u201d platform. Paste as the redirect URI; add Mail.Send + offline_access under API permissions.",
  },
};

// ── Provider config modal ────────────────────────────────────────────

const OAuthAppModal = ({ provider, existing, onClose, onSaved }) => {
  const notify = useNotify();
  const isExisting = existing?.has_secret;
  const [clientID, setClientID] = useState(existing?.client_id || "");
  const [clientSecret, setClientSecret] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [removing, setRemoving] = useState(false);
  // Inline two-step confirm. Never use window.confirm — see
  // feedback_no_browser_popups.md.
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!clientID.trim()) {
      notify({ type: "error", title: "Missing", message: "client_id is required." });
      return;
    }
    if (!isExisting && !clientSecret.trim()) {
      notify({ type: "error", title: "Missing", message: "client_secret is required when configuring a new provider." });
      return;
    }
    setSubmitting(true);
    try {
      const payload = { client_id: clientID.trim() };
      if (clientSecret.trim()) payload.client_secret = clientSecret.trim();
      await upsertOAuthApp(provider.id, payload);
      notify({ type: "success", title: "Saved", message: `${provider.label} OAuth app configured.` });
      onSaved();
      onClose();
    } catch (err) {
      notify({ type: "error", title: "Failed", message: String(err.message || err) });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveConfirmed = async () => {
    setRemoveConfirmOpen(false);
    setRemoving(true);
    try {
      await deleteOAuthApp(provider.id);
      notify({ type: "success", title: "Removed", message: `${provider.label} OAuth app removed.` });
      onSaved();
      onClose();
    } catch (err) {
      notify({ type: "error", title: "Failed", message: String(err.message || err) });
    } finally {
      setRemoving(false);
    }
  };

  const docLink = PROVIDER_DOC_LINKS[provider.id];
  const callbackHint = `${window.location.origin}/api/credentials/oauth/${provider.id}/callback`;
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(callbackHint);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      notify({ type: "error", title: "Copy failed", message: "Browser blocked clipboard access — copy manually." });
    }
  };

  return (
    <div className="gwOverlay" onClick={onClose}>
      <form
        className="gwModal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        style={{ width: 520 }}
      >
        <div className="gwModalHeader">
          <div>
            <div className="gwModalTitle">
              {isExisting ? `Edit ${provider.label} OAuth app` : `Configure ${provider.label}`}
            </div>
            <div className="gwModalSubtitle">
              {docLink ? (
                <>
                  Paste the credentials from{" "}
                  <a href={docLink.consoleURL} target="_blank" rel="noreferrer">
                    {docLink.label} ↗
                  </a>
                  .
                </>
              ) : (
                "Paste the credentials from your provider developer console."
              )}
            </div>
          </div>
          <button className="gwModalClose" type="button" onClick={onClose}>
            <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
          </button>
        </div>

        <div className="gwModalBody">
          <div className="gwField">
            <label className="gwLabel">Redirect URL</label>
            <div className="gwInputCopyRow">
              <input
                className="gwInput"
                readOnly
                value={callbackHint}
                onClick={(e) => e.target.select()}
                style={{ fontFamily: "monospace", fontSize: 12 }}
              />
              <button type="button" className="gwGhostBtn" onClick={handleCopy}>
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <div className="gwHint">
              {docLink?.redirectHint || "Paste this into the redirect URI field of your OAuth app. Must match exactly."}
            </div>
          </div>

          <div className="gwField">
            <label className="gwLabel">Client ID</label>
            <input
              className="gwInput"
              autoFocus
              value={clientID}
              onChange={(e) => setClientID(e.target.value)}
              placeholder="e.g. 1234567890.123456"
            />
          </div>

          <div className="gwField">
            <label className="gwLabel">Client Secret</label>
            <PasswordInput
              placeholder={isExisting ? "Leave blank to keep current secret" : "Paste from provider"}
              value={clientSecret}
              onChange={setClientSecret}
            />
            {isExisting && (
              <div className="gwHint">
                A secret is already saved (encrypted). Leave this blank to keep it; enter a new value to rotate.
              </div>
            )}
          </div>
        </div>

        <div className="gwModalFooter" style={{ justifyContent: "space-between" }}>
          <div>
            {isExisting && (
              <button
                className="gwDangerBtn"
                type="button"
                disabled={removing || submitting}
                onClick={() => setRemoveConfirmOpen(true)}
              >
                {removing ? "Removing..." : "Remove"}
              </button>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="gwGhostBtn" type="button" onClick={onClose}>
              Cancel
            </button>
            <button className="gwPrimaryBtn" type="submit" disabled={submitting}>
              {submitting ? "Saving..." : isExisting ? "Save" : "Configure"}
            </button>
          </div>
        </div>
      </form>
      {removeConfirmOpen && (
        <div
          className="gwOverlay"
          onClick={() => setRemoveConfirmOpen(false)}
          style={{ zIndex: 1100 }}
        >
          <div
            className="gwModal"
            onClick={(e) => e.stopPropagation()}
            style={{ width: 440 }}
          >
            <div className="gwModalHeader">
              <div>
                <div className="gwModalTitle">Remove {provider.label} OAuth app?</div>
              </div>
              <button
                type="button"
                className="gwModalClose"
                onClick={() => setRemoveConfirmOpen(false)}
                aria-label="Close"
              >
                <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
              </button>
            </div>
            <div className="gwModalBody">
              <p style={{ margin: 0 }}>
                Existing credentials connected through this app will stop working. This can't be undone.
              </p>
            </div>
            <div className="gwModalFooter" style={{ justifyContent: "flex-end", gap: 8 }}>
              <button
                className="gwGhostBtn"
                type="button"
                onClick={() => setRemoveConfirmOpen(false)}
              >
                Cancel
              </button>
              <button
                className="gwDangerBtn"
                type="button"
                onClick={handleRemoveConfirmed}
                disabled={removing}
              >
                {removing ? "Removing..." : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────

const OAuthAppsPage = () => {
  const canConfigure = usePermission("configure");
  const [providers, setProviders] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [proposedClientId, setProposedClientId] = useState(null);
  const notify = useNotify();

  const refresh = useCallback(async () => {
    try {
      const [p, c] = await Promise.all([listOAuthProviders(), listOAuthApps()]);
      setProviders(p || []);
      setConfigs(c || []);
    } catch (err) {
      notify({ type: "error", title: "Error", message: String(err.message || err) });
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Refresh when the AI mutates an OAuth app.
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.resource === "oauth_apps") refresh();
    };
    window.addEventListener("gw-ai-resource-changed", handler);
    return () => window.removeEventListener("gw-ai-resource-changed", handler);
  }, [refresh]);

  // AI propose-oauth-app: open the matching provider's modal pre-filled.
  useEffect(() => {
    const handler = (e) => {
      const payload = e.detail?.payload;
      if (!payload || typeof payload !== "object" || !payload.provider) return;
      const match = providers.find(
        (p) => p.id === payload.provider || p.label?.toLowerCase() === String(payload.provider).toLowerCase()
      );
      if (!match) {
        notify({
          type: "error",
          title: "Unknown provider",
          message: `No OAuth provider named "${payload.provider}".`,
        });
        return;
      }
      setProposedClientId(payload.client_id || null);
      setEditing(match);
    };
    window.addEventListener("gw-ai-propose-oauth-app-page", handler);
    return () => window.removeEventListener("gw-ai-propose-oauth-app-page", handler);
  }, [providers, notify]);

  const configByProvider = configs.reduce((acc, c) => {
    acc[c.provider] = c;
    return acc;
  }, {});

  return (
    <div className="gwContentGrid">
      <section className="gwFormStack">
        <div className="gwPageHeader">
          <div>
            <div className="gwPageTitle">OAuth Apps</div>
            <div className="gwPageSubtitle">
              Per-deployment client_id / client_secret per provider. Set these once during gateway
              setup. Individual users then connect their workspaces under{" "}
              <strong>Connectors → Credentials → Connect a service</strong>.
            </div>
          </div>
        </div>

        {loading && (
          <div style={{ padding: 24, textAlign: "center", color: "var(--gw-muted)", fontSize: 13 }}>
            Loading providers...
          </div>
        )}

        {!loading && (
          <div className="gwCardSection">
            <div className="gwSectionTitle">Providers</div>
            <div className="gwCardSectionBody" style={{ padding: 0 }}>
              {providers.map((p) => {
                const cfg = configByProvider[p.id];
                const dbConfigured = !!(cfg && cfg.has_secret);
                const envConfigured = p.configured && !dbConfigured;
                return (
                  <div key={p.id} className="umUserRow"
                    data-gw-resource="oauth_apps" data-gw-id={p.id} data-gw-name={p.label}>
                    <ProviderBadge providerId={p.id} size={36} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="umUserName">{p.label}</div>
                      <div className="umUserMeta">
                        {dbConfigured
                          ? `Configured \u00b7 client_id ${cfg.client_id.slice(0, 8)}\u2026`
                          : envConfigured
                          ? "Configured via environment variables"
                          : "Not configured"}
                      </div>
                    </div>
                    <button
                      type="button"
                      className={dbConfigured ? "gwGhostBtn" : "gwPrimaryBtn"}
                      disabled={!canConfigure}
                      title={canConfigure ? undefined : "Requires configure permission"}
                      onClick={() => setEditing(p)}
                      style={{ flexShrink: 0 }}
                    >
                      {dbConfigured ? "Edit" : envConfigured ? "Override" : "Configure"}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {editing && (
        <OAuthAppModal
          provider={editing}
          existing={
            proposedClientId
              ? { ...(configByProvider[editing.id] || {}), client_id: proposedClientId }
              : configByProvider[editing.id]
          }
          onClose={() => { setEditing(null); setProposedClientId(null); }}
          onSaved={refresh}
        />
      )}
    </div>
  );
};

export { OAuthAppsPage };
